import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseCartValidationProps {
  cartItems: any[];
  digitalSignature: string;
  isCredit: boolean;
  partialPayment: string;
  paymentDueDate: string;
  customerName: string;
  customerContact: string;
  customerAddress: string;
  paymentMethod: string;
  onSuccess: () => void;
}

export const useCartValidation = ({
  cartItems,
  digitalSignature,
  isCredit,
  partialPayment,
  paymentDueDate,
  customerName,
  customerContact,
  customerAddress,
  paymentMethod,
  onSuccess,
}: UseCartValidationProps) => {
  const [loading, setLoading] = useState(false);

  const handleValidateCart = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      for (const item of cartItems) {
        if (item.quantity > item.stock_item.quantity) {
          throw new Error(`Quantité insuffisante pour ${item.stock_item.data.name}`);
        }
      }

      const totalAmount = cartItems.reduce((total, item) => {
        return total + (item.quantity * parseFloat(item.stock_item.data.price));
      }, 0);

      const paidAmount = isCredit ? parseFloat(partialPayment || "0") : totalAmount;
      const remainingAmount = totalAmount - paidAmount;

      for (const item of cartItems) {
        const { error: updateError } = await supabase
          .from('stock_items')
          .update({
            quantity: item.stock_item.quantity - item.quantity
          })
          .eq('id', item.stock_item_id);

        if (updateError) throw updateError;

        const { error: transactionError } = await supabase
          .from('poultry_transactions')
          .insert({
            stock_item_id: item.stock_item_id,
            transaction_type: 'sale',
            quantity: item.quantity,
            unit_price: parseFloat(item.stock_item.data.price),
            total_amount: item.quantity * parseFloat(item.stock_item.data.price),
            transaction_date: new Date().toISOString(),
            created_by: user.id,
            digital_signature: digitalSignature,
            is_fully_paid: !isCredit || paidAmount === totalAmount,
            paid_amount: paidAmount,
            remaining_amount: remainingAmount,
            payment_due_date: isCredit ? paymentDueDate : null,
            notes: isCredit 
              ? `Vente à crédit - Client: ${customerName} (${customerContact}) - Adresse: ${customerAddress}`
              : paymentMethod ? `Paiement par ${paymentMethod}` : null
          });

        if (transactionError) throw transactionError;
      }

      const { error: deleteError } = await supabase
        .from('cart_items')
        .delete()
        .eq('created_by', user.id);

      if (deleteError) throw deleteError;

      toast.success("Vente effectuée avec succès");
      onSuccess();
    } catch (error: any) {
      console.error("Erreur lors de la validation du panier:", error);
      toast.error(error.message || "Erreur lors de la validation du panier");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleValidateCart,
  };
};